import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { InitialsPipe } from '@foxera/core/pipes/initials.pipe';
import { ApplicationRoutes, SWITCH_WORKSPACE_QUERY_PARAM } from '@foxera/shared/constants/application.routes';
import { ProfileDialogComponent } from '@foxera/shared/components/profile-dialog/profile-dialog.component';
import { FoxeraDrawerBottomComponent } from '@foxera/shared/components/foxera-drawer-bottom/foxera-drawer-bottom.component';
import { IProfileData } from '@foxera/shared/interfaces/profile.interface';
import { ProfileService } from '@foxera/shared/services/profile.service';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';

@Component({
  selector: 'app-profile-menu-popover',
  standalone: true,
  imports: [CommonModule, InitialsPipe, ProfileDialogComponent, ButtonModule, PopoverModule, FoxeraDrawerBottomComponent],
  templateUrl: './profile-menu-popover.component.html',
  styleUrl: './profile-menu-popover.component.css',
})
export class ProfileMenuPopoverComponent implements OnInit {
  @ViewChild('profileMenuOverlay') profileMenuOverlay!: Popover;

  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);

  readonly logoutClick = output<void>();
  readonly isLoggingOut = input<boolean>(false);

  showProfileDialog = signal(false);
  profileData: IProfileData | null = null;
  
  // Mobile detection
  isMobile = signal(false);
  mobileDrawerVisible = signal(false);

  public fullName = computed(() => {
    const profile = this.profileService.profileDetails();
    if (!profile?.firstName || !profile?.lastName) {
      return '';
    }
    return `${profile.firstName} ${profile.lastName}`;
  });

  constructor(public profileService: ProfileService) {}

  ngOnInit(): void {
    // Detect mobile breakpoint
    this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .subscribe(result => {
        this.isMobile.set(result.matches);
      });
  }

  toggle(event: MouseEvent): void {
    if (this.isMobile()) {
      this.mobileDrawerVisible.set(true);
    } else {
      this.profileMenuOverlay?.toggle(event);
    }
  }

  hide(): void {
    if (this.isMobile()) {
      this.mobileDrawerVisible.set(false);
    } else {
      this.profileMenuOverlay?.hide();
    }
  }

  onProfileClick(event: Event): void {
    event.stopPropagation();
    this.hide();
    
    // Prepare profile data once when opening dialog
    const profile = this.profileService.profileDetails();
    if (profile) {
      this.profileData = {
        id: String(profile.id || ''),
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phoneNumber: profile.phoneNumber || '',
        email: profile.email || '',
        birthdate: profile.birthdate ? new Date(profile.birthdate) : null,
        profileImagePath: profile.profileImagePath || null,
        timeFormat: profile.timeFormat || '',
      };
    }
    this.showProfileDialog.set(true);
  }

  onProfileDialogVisibilityChange(visible: boolean): void {
    this.showProfileDialog.set(visible);
  }

  onSwitchWorkspaceClick(event: Event): void {
    event.stopPropagation();
    this.hide();
    this.router.navigate([ApplicationRoutes.TENANT_SELECTION], {
      queryParams: { [SWITCH_WORKSPACE_QUERY_PARAM]: true },
    });
  }

  onLogoutClick(event: Event): void {
    event.stopPropagation();
    
    // Check if already logging out
    if (this.isLoggingOut()) {
      return;
    }
    
    this.logoutClick.emit();
  }
}

